import dayjs from '@v4/utils/dayjs'

import useFieldFocus from '@v4/hooks/useFieldFocus'
import useEditorMeta from '@v4/hooks/useEditorMeta'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import AppStore from '@v4/store/appStore'
import { useSectionId } from '@v4/components/common/SectionItem'
import { useMemo } from 'react'
import { BUTTON_STATE, COUNTDOWN_TYPE } from './Cta18.const'
import useStyles from './Cta18.styled'

export const useCta18 = props => {
	const { metaData } = props
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const finishedAt = AppStore.useStoreState(state => state.result.data?.finished_at)
	const activeSectionId = PageBuilderSelector.useActiveSectionId()
	const sectionId = useSectionId()
	const isMinimized = PageBuilderSelector.useIsMinimized(sectionId, 'completedContent', true)

	const items = PageBuilderSelector.useRepeatableGroupKeys({
		sectionId,
		group: 'items',
	})

	const classes = useStyles({ ...props, isBuilderMode })
	const { handleFocus: handleButtonClick } = useFieldFocus({ metaData: metaData.buttonAction })
	const { handleFocus: handleCompletedButtonClick } = useFieldFocus({ metaData: metaData.completedButtonText })
	const { handleFocus: handleTextClick } = useFieldFocus({ metaData: metaData.text })
	const { handleFocus: handleTitleClick } = useFieldFocus({ metaData: metaData.title })
	const { handleFocus: handleAdditionalTextClick } = useFieldFocus({ metaData: metaData.additionalText })
	const { handleFocus: handleCompletedAdditionalTextClick } = useFieldFocus({
		metaData: metaData.completedAdditionalText,
	})
	const { handleFocus: handleDateBlockClick } = useFieldFocus({ metaData: metaData.date })

	const duration = metaData.duration?.metaValue
	const isDurationDate = metaData.countdownType?.metaValue === COUNTDOWN_TYPE.DURATION

	const date = metaData.date?.metaValue

	const utcOffsets = metaData.utcOffsets?.metaValue

	const period = useMemo(() => {
		if (!isDurationDate) {
			return date && utcOffsets
				? dayjs(date)
						.utcOffset(utcOffsets, true)
						.toISOString()
				: date
		}

		const [val, unit] = _.split(duration, ' ')

		const newDate = isBuilderMode ? new Date() : finishedAt

		return unit && val && newDate
			? dayjs(newDate)
					.add(+val, unit)
					.toISOString()
			: undefined
	}, [isDurationDate, duration, isBuilderMode, finishedAt, utcOffsets, date])

	const currentDate = dayjs()
	const isDateExpired = _.isEmpty(period) || currentDate.isAfter(dayjs(period))
	const isButtonStateDisabled = metaData.buttonState?.metaValue !== BUTTON_STATE.ENABLED
	const showCompletedContent = isBuilderMode ? !isMinimized && activeSectionId === sectionId : isDateExpired

	const { editable: textEditable, handleChange: handleTextChange } = useEditorMeta({
		metaData: metaData.text,
		key: 'text',
	})

	const { editable: titleEditable, handleChange: handleTitleChange } = useEditorMeta({
		metaData: metaData.title,
		key: 'title',
	})

	const { editable: buttonTextEditable, handleChange: handleButtonTextChange } = useEditorMeta({
		metaData: metaData.buttonText,
		key: 'buttonText',
	})

	const { editable: completedButtonTextEditable, handleChange: handleCompletedButtonTextChange } = useEditorMeta({
		metaData: metaData.completedButtonText,
		key: 'completedButtonText',
	})

	const { editable: additionalTextEditable, handleChange: handleAdditionalTextChange } = useEditorMeta({
		metaData: metaData.additionalText,
		key: 'additionalText',
	})

	const {
		editable: completedAdditionalTextEditable,
		handleChange: handleCompletedAdditionalTextChange,
	} = useEditorMeta({
		metaData: metaData.completedAdditionalText,
		key: 'completedAdditionalText',
	})

	return [
		{
			items,
			classes,
			date: showCompletedContent ? undefined : period,
			timerColour: metaData.timerColour?.metaValue,
			direction: metaData.imagePosition?.contentDirection(),
			showCompletedContent,
			isButtonStateDisabled,

			title: metaData.title?.metaValue,
			titleConfig: metaData.title?.config,
			titleKey: metaData.title?.tierKey,
			titleEditable,

			text: metaData.text?.metaValue,
			textConfig: metaData.text?.config,
			textKey: metaData.text?.tierKey,
			textEditable,

			buttonAction: metaData.buttonAction?.metaValue,
			buttonLink: metaData.buttonLink?.metaValue,
			buttonText: metaData.buttonText?.metaValue,
			buttonTextConfig: metaData.buttonText?.config,
			buttonTextKey: metaData.buttonText?.tierKey,
			buttonTextEditable,

			additionalText: metaData.additionalText?.metaValue,
			additionalTextConfig: metaData.additionalText?.config,
			additionalTextKey: metaData.additionalText?.tierKey,
			additionalTextEditable,

			completedButtonText: metaData.completedButtonText?.metaValue,
			completedButtonTextConfig: metaData.completedButtonText?.config,
			completedButtonTextKey: metaData.completedButtonText?.tierKey,
			completedButtonTextEditable,
			completedButtonLink: isButtonStateDisabled ? undefined : metaData.completedButtonLink?.metaValue,
			completedButtonAction: isButtonStateDisabled ? undefined : metaData.completedButtonAction?.metaValue,

			completedAdditionalText: metaData.completedAdditionalText?.metaValue,
			completedAdditionalTextConfig: metaData.completedAdditionalText?.config,
			completedAdditionalTextKey: metaData.completedAdditionalText?.tierKey,
			completedAdditionalTextEditable,
		},
		{
			handleButtonClick,
			handleButtonTextChange,

			handleCompletedButtonClick,
			handleCompletedButtonTextChange,

			handleTextClick,
			handleTextChange,

			handleTitleClick,
			handleTitleChange,

			handleAdditionalTextClick,
			handleAdditionalTextChange,

			handleCompletedAdditionalTextClick,
			handleCompletedAdditionalTextChange,

			handleDateBlockClick,
		},
	]
}
